import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  PopoverCoordinator,
  PopoverTransferProps,
} from '../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {createSelector} from '../../../../../srcCommon/helpers/reselectHelpers';
import {useAppSelector} from '../../../../helpers/appReduxHelpers';
import {getCounters} from '../../../../store/data/dataSelectors';
import {TopBarButton} from '../topBarButton';
import {NotificationCenterDropdownContainer} from './dropdown/notificationCenterDropdownContainer';

/*
 * Style.
 */

const StyledNotificationsCenterWrapperDiv = styled.div`
  position: relative;
`;

const StyledCounterDiv = styled.div`
  position: absolute;

  top: 1px;
  left: 14px;

  height: 12px;
  min-width: 12px;
  padding: 0 3px;

  background-color: ${(p) => p.theme.palette.blue.shade40};
  border-radius: 6px 6px;

  color: ${(p) => p.theme.greys.white};
  font-size: ${fontSizes.veryTiny};
  font-weight: ${fontWeights.semibold};
  line-height: ${fontSizes.verySmall};
  text-align: center;
`;

/*
 * Component.
 */

export const NotificationsCenter: FC = () => {
  const notificationsCounter = useAppSelector(getNotificationsCounter);

  const renderNotificationsButton = (isDropdownOpen: boolean) => (
    <StyledNotificationsCenterWrapperDiv>
      <TopBarButton
        iconName="lightning"
        isSelected={isDropdownOpen}
        renderTooltip={() => <Trans id="nc-button">Notifications</Trans>}
      >
        <Trans id="nc-button">Notifications</Trans>
      </TopBarButton>
      {maybeRenderCounter(notificationsCounter)}
    </StyledNotificationsCenterWrapperDiv>
  );

  const renderNotificationsDropdown = (popoverProps: PopoverTransferProps) => (
    <NotificationCenterDropdownContainer
      {...popoverProps}
      placement="top-end"
      notificationsCounter={notificationsCounter}
    />
  );

  return (
    <PopoverCoordinator
      renderButton={(isOpen) => renderNotificationsButton(isOpen)}
      renderModal={(popoverProps) => renderNotificationsDropdown(popoverProps)}
    />
  );
};

/*
 * Helpers.
 */

const getNotificationsCounter = createSelector([getCounters], (counters) => counters.notifications);

const maybeRenderCounter = (notificationsCounter: number) => {
  if (notificationsCounter === 0) {
    return null;
  }

  const count = notificationsCounter > 99 ? '99+' : notificationsCounter;
  return <StyledCounterDiv>{count}</StyledCounterDiv>;
};
